import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  Chip,
  Alert,
  Snackbar,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import { Grid } from '../components/common/TypedGrid';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Place as PlaceIcon,
  CheckCircle as ActiveIcon,
  Cancel as InactiveIcon,
  Assessment as StatsIcon,
} from '@mui/icons-material';
import adminService from '../services/adminService';
import { PageHeader, ResponsiveTable, StatCard, StatusChip } from '../components/common';

interface ServiceableArea {
  id: string;
  areaName: string;
  maxDeliveryRadius: number;
  isActive: boolean;
  order: number;
  boundaries?: {
    type: 'circle';
    center: {
      latitude: number;
      longitude: number;
    };
    radius: number;
  };
  createdAt?: Date;
  updatedAt?: Date;
}

interface AreaFormData {
  areaName: string;
  maxDeliveryRadius: number;
  isActive: boolean;
  order: number;
  centerLatitude: number;
  centerLongitude: number;
}

interface AreaStatistics {
  totalAreas: number;
  activeAreas: number;
  inactiveAreas: number;
  totalTasks?: number;
}

const AreaManagement: React.FC = () => {
  const [areas, setAreas] = useState<ServiceableArea[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedArea, setSelectedArea] = useState<ServiceableArea | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [globalMaxRadius, setGlobalMaxRadius] = useState<number>(50); // Default fallback
  
  const [formData, setFormData] = useState<AreaFormData>({
    areaName: '',
    maxDeliveryRadius: 30,
    isActive: true,
    order: 1,
    centerLatitude: 20.2961,
    centerLongitude: 85.8245,
  });

  const [formErrors, setFormErrors] = useState<Partial<Record<keyof AreaFormData, string>>>({});

  useEffect(() => {
    loadAreas();
    loadGlobalSettings();
  }, []);

  const loadGlobalSettings = async () => {
    try {
      const settings = await adminService.getAppSettings();
      if (settings?.generalSettings?.maxDeliveryRadius) {
        setGlobalMaxRadius(settings.generalSettings.maxDeliveryRadius);
        console.log('📍 Global max delivery radius loaded:', settings.generalSettings.maxDeliveryRadius, 'km');
      }
    } catch (error) {
      console.error('Error loading global settings:', error);
    }
  };

  const loadAreas = async () => {
    setLoading(true);
    try {
      const fetchedAreas = await adminService.getAllServiceableAreas();
      setAreas(fetchedAreas);
    } catch (error) {
      console.error('Error loading areas:', error);
      showSnackbar('Error loading areas', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (area?: ServiceableArea) => {
    if (area) {
      setEditMode(true);
      setSelectedArea(area);
      setFormData({
        areaName: area.areaName,
        maxDeliveryRadius: area.maxDeliveryRadius,
        isActive: area.isActive,
        order: area.order,
        centerLatitude: area.boundaries?.center?.latitude || 20.2961,
        centerLongitude: area.boundaries?.center?.longitude || 85.8245,
      });
    } else {
      setEditMode(false);
      setSelectedArea(null);
      setFormData({
        areaName: '',
        maxDeliveryRadius: 30,
        isActive: true,
        order: areas.length + 1,
        centerLatitude: 20.2961,
        centerLongitude: 85.8245,
      });
    }
    setFormErrors({});
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedArea(null);
    setFormErrors({});
  };

  const validateForm = (): boolean => {
    const errors: Partial<Record<keyof AreaFormData, string>> = {};

    if (!formData.areaName || formData.areaName.trim().length < 3) {
      errors.areaName = 'Area name must be at least 3 characters';
    }

    if (formData.maxDeliveryRadius <= 0) {
      errors.maxDeliveryRadius = 'Radius must be greater than 0 km';
    } else if (formData.maxDeliveryRadius > globalMaxRadius) {
      errors.maxDeliveryRadius = `Radius cannot exceed global max delivery radius (${globalMaxRadius} km). Update global settings first.`;
    }

    if (formData.order < 1) {
      errors.order = 'Order must be at least 1';
    }

    if (formData.centerLatitude < -90 || formData.centerLatitude > 90) {
      errors.centerLatitude = 'Latitude must be between -90 and 90';
    }

    if (formData.centerLongitude < -180 || formData.centerLongitude > 180) {
      errors.centerLongitude = 'Longitude must be between -180 and 180';
    }

    // Check for duplicate area name (excluding current area in edit mode)
    const duplicateArea = areas.find(
      area => 
        area.areaName.toLowerCase() === formData.areaName.trim().toLowerCase() &&
        (!editMode || area.id !== selectedArea?.id)
    );
    if (duplicateArea) {
      errors.areaName = 'Area name already exists';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      const areaData = {
        areaName: formData.areaName.trim(),
        maxDeliveryRadius: formData.maxDeliveryRadius,
        isActive: formData.isActive,
        order: formData.order,
        boundaries: {
          type: 'circle' as const,
          center: {
            latitude: formData.centerLatitude,
            longitude: formData.centerLongitude,
          },
          radius: formData.maxDeliveryRadius,
        },
      };

      if (editMode && selectedArea) {
        await adminService.updateServiceableArea(selectedArea.id, areaData);
        showSnackbar('Area updated successfully', 'success');
      } else {
        await adminService.createServiceableArea(areaData);
        showSnackbar('Area created successfully', 'success');
      }

      handleCloseDialog();
      await loadAreas();
    } catch (error) {
      console.error('Error saving area:', error);
      showSnackbar('Error saving area', 'error');
    }
  };

  const handleDeleteArea = async () => {
    if (!selectedArea) return;

    try {
      await adminService.deleteServiceableArea(selectedArea.id);
      showSnackbar('Area deleted successfully', 'success');
      setDeleteDialogOpen(false);
      setSelectedArea(null);
      await loadAreas();
    } catch (error) {
      console.error('Error deleting area:', error);
      showSnackbar('Error deleting area', 'error');
    }
  };

  const handleToggleActive = async (area: ServiceableArea) => {
    try {
      await adminService.updateServiceableArea(area.id, {
        isActive: !area.isActive,
      });
      showSnackbar(`Area ${!area.isActive ? 'activated' : 'deactivated'} successfully`, 'success');
      await loadAreas();
    } catch (error) {
      console.error('Error updating area status:', error);
      showSnackbar('Error updating area status', 'error');
    }
  };

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbar({ open: true, message, severity });
  };

  const getStatistics = (): AreaStatistics => {
    return {
      totalAreas: areas.length,
      activeAreas: areas.filter(a => a.isActive).length,
      inactiveAreas: areas.filter(a => !a.isActive).length,
    };
  };

  const stats = getStatistics();

  return (
    <Box>
      <PageHeader
        title="Areas"
        subtitle="Where the service operates"
        actions={
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()}>
            Add New Area
          </Button>
        }
      />

      <Grid container spacing={{ xs: 1.5, md: 3 }} sx={{ mb: 3 }}>
        <Grid item xs={4} md={4}>
          <StatCard label="Total Areas" value={stats.totalAreas} icon={<PlaceIcon />} tone="primary" />
        </Grid>
        <Grid item xs={4} md={4}>
          <StatCard label="Active" value={stats.activeAreas} icon={<ActiveIcon />} tone="success" />
        </Grid>
        <Grid item xs={4} md={4}>
          <StatCard label="Inactive" value={stats.inactiveAreas} icon={<InactiveIcon />} tone="error" />
        </Grid>
      </Grid>

      {/* Areas Table */}
      <Card>
        <CardContent>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : areas.length === 0 ? (
            <Alert severity="info">
              No serviceable areas found. Click "Add New Area" to create one.
            </Alert>
          ) : (
            <ResponsiveTable
              columns={[
                {
                  key: 'order',
                  header: 'Order',
                  width: 80,
                  render: (area: ServiceableArea) => area.order,
                },
                {
                  key: 'name',
                  header: 'Area Name',
                  mobile: 'title',
                  render: (area: ServiceableArea) => (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PlaceIcon color="primary" />
                      <Typography fontWeight={600}>{area.areaName}</Typography>
                    </Box>
                  ),
                },
                {
                  key: 'radius',
                  header: 'Max Radius',
                  render: (area: ServiceableArea) => `${area.maxDeliveryRadius} km`,
                },
                {
                  key: 'center',
                  header: 'Center Coordinates',
                  render: (area: ServiceableArea) =>
                    area.boundaries?.center ? (
                      <Tooltip title="Open in Google Maps">
                        <Chip
                          label={`${area.boundaries.center.latitude.toFixed(4)}, ${area.boundaries.center.longitude.toFixed(4)}`}
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            window.open(
                              `https://www.google.com/maps?q=${area.boundaries!.center.latitude},${area.boundaries!.center.longitude}`,
                              '_blank',
                              'noopener,noreferrer'
                            );
                          }}
                          sx={{ cursor: 'pointer' }}
                        />
                      </Tooltip>
                    ) : (
                      'N/A'
                    ),
                },
                {
                  key: 'status',
                  header: 'Status',
                  render: (area: ServiceableArea) => (
                    <StatusChip status={area.isActive ? 'active' : 'inactive'} />
                  ),
                },
                {
                  key: 'actions',
                  header: 'Actions',
                  align: 'right',
                  mobile: 'actions',
                  render: (area: ServiceableArea) => (
                    <>
                      <Tooltip title={area.isActive ? 'Deactivate' : 'Activate'}>
                        <IconButton
                          size="small"
                          onClick={() => handleToggleActive(area)}
                          color={area.isActive ? 'success' : 'error'}
                          aria-label={area.isActive ? 'Deactivate area' : 'Activate area'}
                        >
                          {area.isActive ? <ActiveIcon /> : <InactiveIcon />}
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          onClick={() => handleOpenDialog(area)}
                          color="primary"
                          aria-label="Edit area"
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setSelectedArea(area);
                            setDeleteDialogOpen(true);
                          }}
                          color="error"
                          aria-label="Delete area"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </>
                  ),
                },
              ]}
              rows={[...areas].sort((a, b) => a.order - b.order)}
              rowKey={(area) => area.id}
              aria-label="Serviceable areas"
              emptyTitle="No serviceable areas"
              emptyDescription="Add an area to define where the service operates."
            />
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editMode ? '✏️ Edit Area' : '➕ Add New Area'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            {/* Global Max Radius Info */}
            <Alert severity="info" sx={{ mb: 1 }}>
              <strong>Global Max Delivery Radius:</strong> {globalMaxRadius} km
              <br />
              <Typography variant="caption">
                Area radius cannot exceed this value. Update in Settings if needed.
              </Typography>
            </Alert>
            
            <TextField
              label="Area Name"
              value={formData.areaName}
              onChange={(e) => setFormData({ ...formData, areaName: e.target.value })}
              error={!!formErrors.areaName}
              helperText={formErrors.areaName || 'e.g., Bhubaneswar, Cuttack'}
              fullWidth
              required
            />

            <TextField
              label="Max Delivery Radius (km)"
              type="number"
              value={formData.maxDeliveryRadius}
              onChange={(e) => setFormData({ ...formData, maxDeliveryRadius: Number(e.target.value) })}
              error={!!formErrors.maxDeliveryRadius}
              helperText={formErrors.maxDeliveryRadius || `Maximum allowed: ${globalMaxRadius} km (set in Settings → Max Delivery Radius)`}
              fullWidth
              required
              inputProps={{ min: 1, max: globalMaxRadius, step: 1 }}
            />

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Center Latitude"
                type="number"
                value={formData.centerLatitude}
                onChange={(e) => setFormData({ ...formData, centerLatitude: Number(e.target.value) })}
                error={!!formErrors.centerLatitude}
                helperText={formErrors.centerLatitude}
                fullWidth
                required
                inputProps={{ min: -90, max: 90, step: 0.0001 }}
              />
              <TextField
                label="Center Longitude"
                type="number"
                value={formData.centerLongitude}
                onChange={(e) => setFormData({ ...formData, centerLongitude: Number(e.target.value) })}
                error={!!formErrors.centerLongitude}
                helperText={formErrors.centerLongitude}
                fullWidth
                required
                inputProps={{ min: -180, max: 180, step: 0.0001 }}
              />
            </Box>

            <TextField
              label="Display Order"
              type="number"
              value={formData.order}
              onChange={(e) => setFormData({ ...formData, order: Number(e.target.value) })}
              error={!!formErrors.order}
              helperText={formErrors.order || 'Order in which area appears in dropdown'}
              fullWidth
              required
              inputProps={{ min: 1, step: 1 }}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  color="success"
                />
              }
              label={
                <Box>
                  <Typography variant="body1">Active Status</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formData.isActive ? 'Area is available for bookings' : 'Area is hidden from customers'}
                  </Typography>
                </Box>
              }
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" color="primary">
            {editMode ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>🗑️ Delete Area</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Are you sure you want to delete <strong>{selectedArea?.areaName}</strong>?
            This action cannot be undone.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteArea} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AreaManagement;
